import { Injectable, signal } from '@angular/core';
import { ProductSalesReportItem } from 'src/app/models/core/product.model';
import { GlobalReportApiService } from './global-report-api.service';
import { HeaderKpisService } from './header-kpis.service';
import { IsLatestRequest, latestRequestTracker } from 'src/app/utils/reports/latest-request';

export interface ProductCategory {
  id: number;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class ProductReportService {
  readonly soldProductReportLoading = signal(false);
  readonly soldProductReportData = signal<ProductSalesReportItem[]>([]);

  readonly currentPage = signal(1);
  readonly lastPage = signal(1);
  readonly total = signal(0);
  readonly showLoadMore = signal(false);
  readonly isLoadingMore = signal(false);

  readonly productsSortOrder = signal<'most_sold' | 'least_sold'>('most_sold');

  /** Opciones de orden; `labelKey` se traduce donde se muestra. */
  readonly productsSortOptions = [
    { labelKey: 'sales-breakdown.sort.most-sold', value: 'most_sold' },
    { labelKey: 'sales-breakdown.sort.least-sold', value: 'least_sold' },
  ];

  private readonly initialRequest = latestRequestTracker();
  /** Lista vigente: una recarga (rol, orden, búsqueda) la invalida junto con sus "Cargar más". */
  private isCurrentList: IsLatestRequest = this.initialRequest.start();

  constructor(
    private globalReportApi: GlobalReportApiService,
    private headerKpisService: HeaderKpisService,
  ) {}

  changeSortOrder(order: 'most_sold' | 'least_sold'): void {
    if (this.productsSortOrder() === order) return;
    this.productsSortOrder.set(order);
    this.loadInitialProducts();
  }

  getSelectedSortLabelKey(): string {
    return this.productsSortOptions.find(
      opt => opt.value === this.productsSortOrder()
    )?.labelKey || '';
  }

  loadInitialProducts(): void {
    const eventId = this.headerKpisService.eventId;
    if (!eventId) return;

    this.soldProductReportLoading.set(true);
    // Al cambiar de rol se pide de nuevo: la respuesta del rol anterior no pisa la nueva.
    const isLatest = this.initialRequest.start();
    this.isCurrentList = isLatest;
    this.isLoadingMore.set(false);
    this.currentPage.set(1);
    this.lastPage.set(1);
    this.total.set(0);
    this.soldProductReportData.set([]);

    this.globalReportApi
      .getRankedProducts(eventId, this.productsSortOrder(), 1, 10)
      .subscribe({
        next: ({ data = [], meta = {} }) => {
          if (!isLatest()) return;
          this.updateProductsList(data, meta, true);
        },
        error: () => {
          if (!isLatest()) return;
          this.soldProductReportLoading.set(false);
        },
        complete: () => {
          if (!isLatest()) return;
          this.soldProductReportLoading.set(false);
        },
      });
  }

  loadMoreProducts(): void {
    const nextPage = this.currentPage() + 1;
    if (nextPage > this.lastPage()) {
      this.showLoadMore.set(false);
      return;
    }

    const eventId = this.headerKpisService.eventId;
    if (!eventId) return;

    this.isLoadingMore.set(true);
    const isLatest = this.isCurrentList;

    this.globalReportApi
      .getRankedProducts(eventId, this.productsSortOrder(), nextPage, 10)
      .subscribe({
        next: ({ data = [], meta = {} }) => {
          if (!isLatest()) return;
          this.updateProductsList(data, meta, false);
        },
        error: () => {
          if (!isLatest()) return;
          this.isLoadingMore.set(false);
        },
        complete: () => {
          if (!isLatest()) return;
          this.isLoadingMore.set(false);
        },
      });
  }

  private updateProductsList(
    newProducts: ProductSalesReportItem[],
    meta: { current_page?: number; last_page?: number; total?: number },
    replace: boolean,
  ): void {
    if (replace) {
      this.soldProductReportData.set(newProducts);
    } else {
      const currentIds = new Set(this.soldProductReportData().map(p => p.id));
      const merged = [
        ...this.soldProductReportData(),
        ...newProducts.filter(p => !currentIds.has(p.id)),
      ];
      this.soldProductReportData.set(merged);
    }

    this.currentPage.set(meta.current_page ?? 1);
    this.lastPage.set(meta.last_page ?? 1);
    if (meta.total !== undefined) {
      this.total.set(meta.total);
    }
    this.showLoadMore.set((meta.current_page ?? 1) < (meta.last_page ?? 1));
  }

  resetAllState(): void {
    this.soldProductReportData.set([]);
    this.soldProductReportLoading.set(false);
    this.currentPage.set(1);
    this.lastPage.set(1);
    this.total.set(0);
    this.showLoadMore.set(false);
    this.isLoadingMore.set(false);
  }
}
